/**
 * Maps each landing tile to parent category *names* — keys in
 * GET /categories?type=IDEA_STARTER (`data["Innovation"]`, etc.).
 */

export type IdeaStarterTileDef = {
  label: string;
  slug: string;
  /** Keys into `AreaOfInterestResponse.data` (parent Category.name). */
  parentCategoryNames: string[];
};

export const IDEA_STARTER_TILES: IdeaStarterTileDef[] = [
  {
    label: 'Workplace Innovation',
    slug: 'workplace-innovation',
    parentCategoryNames: [
      'Innovation',
      'Planning for Innovation and Competitive Edge',
      'Emerging Technologies and Innovation',
      'Standardization vs. Innovation',
      'Entrepreneurship and Business Dynamics',
      'Artificial Intelligence',
      'AI Disruption',
    ],
  },
  {
    label: 'Talent and Culture',
    slug: 'talent-and-culture',
    parentCategoryNames: [
      'Managing Change in the Workplace',
      'Role of Management in Innovation and Change',
    ],
  },
  {
    label: 'Change Management',
    slug: 'change-management',
    parentCategoryNames: [
      'Managing Change in the Workplace',
      'Change Management in the Context of Emerging Technologies',
      'Intersection of Change Management and Project Management',
      'Role of Management in Innovation and Change',
      'Artificial Intelligence',
    ],
  },
  {
    label: 'Risk and Resilience',
    slug: 'risk-and-resilience',
    parentCategoryNames: [
      'Managing Change in the Workplace',
      'Standardization vs. Innovation',
      'Intersection of Change Management and Project Management',
      'AI Disruption',
    ],
  },
  {
    label: 'Leadership and Growth',
    slug: 'leadership-and-growth',
    parentCategoryNames: [
      'Role of Management in Innovation and Change',
      'Managing Change in the Workplace',
      'Entrepreneurship and Business Dynamics',
    ],
  },
  {
    label: 'Sustainability Impact',
    slug: 'sustainability-impact',
    parentCategoryNames: [
      'Emerging Technologies and Innovation',
      'Planning for Innovation and Competitive Edge',
      'AI in Energy Management',
    ],
  },
  {
    label: 'Operational Excellence',
    slug: 'operational-excellence',
    parentCategoryNames: [
      'Intersection of Change Management and Project Management',
      'Planning for Innovation and Competitive Edge',
      'Standardization vs. Innovation',
    ],
  },
  {
    label: 'Customer Success',
    slug: 'customer-success',
    parentCategoryNames: [
      'Entrepreneurship and Business Dynamics',
      'Innovation',
      'AI in Healthcare',
      'AI in Investment and Finance',
    ],
  },
  {
    label: 'Artificial Intelligence',
    slug: 'artificial-intelligence',
    parentCategoryNames: [
      'Artificial Intelligence',
      'AI in Healthcare',
      'AI in Medical Diagnosis',
      'AI in Investment and Finance',
      'AI Disruption',
      'AI in Energy Management',
    ],
  },
];

export function getParentCategoryNamesForStarterSlug(
  slug: string | null
): string[] | undefined {
  if (!slug) return undefined;
  const tile = IDEA_STARTER_TILES.find((t) => t.slug === slug);
  return tile?.parentCategoryNames;
}

export function isKnownStarterSlug(slug: string | null): boolean {
  if (!slug) return false;
  return IDEA_STARTER_TILES.some((t) => t.slug === slug);
}

type CategoryGroup = { id: string; name: string; parentCategoryId?: string };

/** All subcategory ids under the tile's parent groups (for API + checkbox state). */
export function resolveStarterToSubcategoryIds(
  slug: string,
  categoriesByParent: Record<string, CategoryGroup[]> | undefined
): string[] {
  if (!categoriesByParent) return [];
  const parentNames = getParentCategoryNamesForStarterSlug(slug);
  if (!parentNames?.length) return [];

  const ids: string[] = [];
  for (const name of parentNames) {
    const subs = categoriesByParent[name];
    if (subs) {
      for (const s of subs) ids.push(s.id);
    }
  }
  return [...new Set(ids)];
}
